import { isAuthError } from "@supabase/supabase-js";
import type { AuthError, Session, User } from "@supabase/supabase-js";

import { type AuthLinkToken, isRecoveryToken } from "../../hooks/auth/authLinkToken";
import supabase, {
  signIn as supabaseSignIn,
  signOut as supabaseSignOut,
  updatePassword as supabaseUpdatePassword,
} from "../../services/supabaseClient";
import {
  beginDeliberateSignOut,
  beginPasswordResetFlagWrite,
  beginRecoveryConfirm,
  beginUpdateUserOp,
  cancelDeliberateSignOut,
  cancelRecoveryConfirm,
  currentGeneration,
  dispatchAuthEvent,
  endPasswordResetFlagWrite,
  endUpdateUserOp,
  setAuthSession,
} from "./store";
import { PASSWORD_RESET_REQUIRED_KEY } from "./types";

/** Exhaustiveness backstop: fails loudly if a future AuthLinkToken variant is
 *  added without a corresponding case here, instead of silently doing nothing. */
const assertNever = (value: never): never => {
  throw new Error(`[authMachine] unhandled auth link token type: ${JSON.stringify(value)}`);
};

const exchangeToken = (
  token: AuthLinkToken
): Promise<{ data: { session: Session | null; user: User | null }; error: AuthError | null }> => {
  switch (token.type) {
    case "code":
      return supabase.auth.exchangeCodeForSession(token.code);
    case "token_hash":
      return supabase.auth.verifyOtp({ token_hash: token.tokenHash, type: token.otpType });
    case "hash":
      return supabase.auth.setSession({
        access_token: token.accessToken,
        refresh_token: token.refreshToken,
      });
    default:
      return assertNever(token);
  }
};

/**
 * Async operations the machine starts. Each stamps the generation at start;
 * the reducer drops results whose stamp no longer matches (stale by construction).
 * Operations also return/throw to their callers so forms can render errors —
 * the machine and the caller observe the same outcome through different channels.
 */
export const authOperations = {
  signIn: async (
    email: string,
    password: string
  ): Promise<{ user: User | null; session: Session | null; error: Error | null }> => {
    // SIGNED_IN is broadcast (and handled by the wiring) before this resolves;
    // the machine treats the redundant path idempotently.
    return supabaseSignIn(email, password);
  },

  signOut: async (): Promise<{ error: AuthError | null }> => {
    beginDeliberateSignOut();
    try {
      const { error } = await supabaseSignOut();
      if (error) {
        cancelDeliberateSignOut();
      }
      return { error };
    } catch (error) {
      cancelDeliberateSignOut();
      throw error;
    }
  },

  updatePassword: async (password: string): Promise<{ user: User }> => {
    const generation = currentGeneration();
    beginUpdateUserOp();
    try {
      const result = await supabaseUpdatePassword(password);
      if (result.user) {
        dispatchAuthEvent({ type: "PASSWORD_UPDATED", user: result.user, generation });
      }
      return result as { user: User };
    } catch (error) {
      dispatchAuthEvent({ type: "PASSWORD_UPDATE_FAILED", generation });
      throw error;
    } finally {
      endUpdateUserOp();
    }
  },

  /** Entry effect for passwordResetPending: server-side flag write. One round-trip —
   *  uses updateUser's own response (no follow-up getUser). */
  writePasswordResetRequiredFlag: async (): Promise<void> => {
    // Self-gating: at most one write in flight. Any caller — the store's entry
    // effect, the store's retry wiring, future code — is protected here, so
    // call sites don't need their own race-aware guards.
    if (!beginPasswordResetFlagWrite()) {
      return;
    }
    const generation = currentGeneration();
    beginUpdateUserOp();
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: { [PASSWORD_RESET_REQUIRED_KEY]: true },
      });
      if (error || !data.user) {
        console.error("[authMachine] Failed to write password_reset_required:", error);
        dispatchAuthEvent({ type: "PASSWORD_RESET_FLAG_WRITE_FAILED", generation });
        return;
      }
      dispatchAuthEvent({
        type: "PASSWORD_RESET_FLAG_WRITE_SUCCEEDED",
        user: data.user,
        generation,
      });
    } catch (error) {
      console.error("[authMachine] password_reset_required write threw unexpectedly:", error);
      dispatchAuthEvent({ type: "PASSWORD_RESET_FLAG_WRITE_FAILED", generation });
    } finally {
      endUpdateUserOp();
      endPasswordResetFlagWrite();
    }
  },

  /** Explicit-click-gated link confirmation (code, hash, and token_hash tokens). */
  confirmAuthLink: async (token: AuthLinkToken): Promise<{ error: { code?: string } | null }> => {
    if (isRecoveryToken(token)) {
      beginRecoveryConfirm();
    }
    try {
      const { error } = await exchangeToken(token);
      if (error) {
        cancelRecoveryConfirm();
        return { error: { code: isAuthError(error) ? error.code : undefined } };
      }
      return { error: null };
    } catch (error) {
      console.error("[authMachine] confirmAuthLink threw unexpectedly:", error);
      cancelRecoveryConfirm();
      return { error: { code: isAuthError(error) ? error.code : undefined } };
    }
  },

  enterPasswordReset: (): void => {
    dispatchAuthEvent({ type: "PASSWORD_RESET_ENTERED" });
  },

  markSignInComplete: (): void => {
    dispatchAuthEvent({ type: "SIGN_IN_MARKED_COMPLETE" });
  },

  /**
   * EmailConfirmation's poll finds a session directly via supabase.auth.getSession()
   * once the user confirms their email in another tab — Supabase does not re-broadcast
   * that discovery through onAuthStateChange, so the caller notifies the machine
   * explicitly instead of relying on the wiring.
   */
  notifyEmailConfirmed: (session: Session): void => {
    setAuthSession(session);
    dispatchAuthEvent({ type: "EMAIL_CONFIRMED", user: session.user });
  },
};
